import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { WhaticketOfficialForm } from './WhaticketOfficialForm';
import { NotificamehubOfficialForm } from './NotificamehubOfficialForm';
import type { ChatChannel, ChatProvider, ChatImplementation } from '@/types/channels';

interface ChannelWizardProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface ChannelOption {
  value: ChatChannel;
  label: string;
  description: string;
  icon: string;
}

interface ProviderOption {
  value: ChatProvider;
  label: string;
  description: string;
}

interface ImplementationOption {
  value: ChatImplementation;
  label: string;
  description: string;
  recommended?: boolean;
}

const CHANNEL_OPTIONS: ChannelOption[] = [
  {
    value: 'WHATSAPP' as ChatChannel,
    label: 'WhatsApp',
    description: 'Converse com seus clientes pelo WhatsApp',
    icon: '💬',
  },
  {
    value: 'INSTAGRAM' as ChatChannel,
    label: 'Instagram',
    description: 'Gerencie mensagens diretas do Instagram',
    icon: '📷',
  },
];

const PROVIDER_OPTIONS: Record<ChatChannel, ProviderOption[]> = {
  WHATSAPP: [
    {
      value: 'WHATICKET' as ChatProvider,
      label: 'Whaticket',
      description: 'Plataforma de atendimento multicanal',
    },
    {
      value: 'WAHA' as ChatProvider,
      label: 'WAHA',
      description: 'WhatsApp HTTP API (auto-hospedado)',
    },
    {
      value: 'NOTIFICAMEHUB' as ChatProvider,
      label: 'NotificameHub',
      description: 'Gateway de notificações via WhatsApp',
    },
  ],
  INSTAGRAM: [
    {
      value: 'META' as ChatProvider,
      label: 'Meta',
      description: 'API oficial do Instagram/Facebook',
    },
  ],
  WEB: [],
};

const IMPLEMENTATION_OPTIONS: Record<string, ImplementationOption[]> = {
  'WHATSAPP-WHATICKET': [
    {
      value: 'WHATSAPP_CLOUD' as ChatImplementation,
      label: 'WhatsApp Cloud API (Oficial)',
      description: 'API oficial do WhatsApp Business',
      recommended: true,
    },
    {
      value: 'BAILEYS' as ChatImplementation,
      label: 'Baileys',
      description: 'Biblioteca não oficial (maior risco)',
    },
  ],
  'WHATSAPP-WAHA': [
    {
      value: 'WHATSAPP_CLOUD' as ChatImplementation,
      label: 'WhatsApp Cloud API',
      description: 'API oficial via WAHA',
      recommended: true,
    },
    {
      value: 'BAILEYS' as ChatImplementation,
      label: 'Baileys',
      description: 'Multi-device via WAHA',
    },
  ],
  'WHATSAPP-NOTIFICAMEHUB': [
    {
      value: 'WHATSAPP_CLOUD' as ChatImplementation,
      label: 'WhatsApp Cloud API (Oficial)',
      description: 'API oficial do WhatsApp Business via NotificameHub',
      recommended: true,
    },
  ],
  'INSTAGRAM-META': [
    {
      value: 'INSTAGRAM_MESSAGING' as ChatImplementation,
      label: 'Instagram Messaging',
      description: 'API oficial de mensagens',
      recommended: true,
    },
  ],
  'MERCADO_LIVRE-NOTIFICAMEHUB': [
    {
      value: 'MERCADO_LIVRE_MESSAGING' as ChatImplementation,
      label: 'Mercado Livre Messaging',
      description: 'API de mensagens do ML',
      recommended: true,
    },
  ],
};

export function ChannelWizard({ open, onClose, onSuccess }: ChannelWizardProps) {
  const [step, setStep] = useState(1);
  const [selectedChannel, setSelectedChannel] = useState<ChatChannel | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<ChatProvider | null>(null);
  const [selectedImplementation, setSelectedImplementation] = useState<ChatImplementation | null>(null);

  const handleChannelSelect = (channel: ChatChannel) => {
    setSelectedChannel(channel);
    setSelectedProvider(null);
    setSelectedImplementation(null);
    setStep(2);
  };

  const handleProviderSelect = (provider: ChatProvider) => {
    setSelectedProvider(provider);
    setSelectedImplementation(null);
    setStep(3);
  };

  const handleImplementationSelect = (implementation: ChatImplementation) => {
    setSelectedImplementation(implementation);
    setStep(4);
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedChannel(null);
    setSelectedProvider(null);
    setSelectedImplementation(null);
    onClose();
  };

  const handleFormSuccess = () => {
    handleClose();
    onSuccess?.();
  };

  const getImplementationOptions = (): ImplementationOption[] => {
    if (!selectedChannel || !selectedProvider) return [];
    const key = `${selectedChannel}-${selectedProvider}`;
    return IMPLEMENTATION_OPTIONS[key] || [];
  };

  const getProviderOptions = (): ProviderOption[] => {
    if (!selectedChannel) return [];
    return PROVIDER_OPTIONS[selectedChannel] || [];
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Adicionar Canal</DialogTitle>
          <DialogDescription>
            Configure uma nova conexão de mensagens em {step === 4 ? '4' : step} de 4 etapas
          </DialogDescription>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-2 py-4">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`h-2 flex-1 rounded-full transition-colors ${
                s <= step ? 'bg-primary' : 'bg-muted'
              }`}
            />
          ))}
        </div>

        {/* Step 1: Select Channel */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Escolha o canal</h3>
              <p className="text-sm text-muted-foreground">
                Selecione qual plataforma de mensagens você deseja conectar
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CHANNEL_OPTIONS.map((option) => (
                <Card
                  key={option.value}
                  className="cursor-pointer hover:border-primary transition-colors"
                  onClick={() => handleChannelSelect(option.value)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{option.icon}</span>
                      <div>
                        <CardTitle className="text-base">{option.label}</CardTitle>
                        <CardDescription className="text-xs mt-1">
                          {option.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Select Provider */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Escolha o provedor</h3>
              <p className="text-sm text-muted-foreground">
                Selecione qual provedor/plataforma você está usando
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {getProviderOptions().map((option) => (
                <Card
                  key={option.value}
                  className="cursor-pointer hover:border-primary transition-colors"
                  onClick={() => handleProviderSelect(option.value)}
                >
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{option.label}</CardTitle>
                    <CardDescription className="text-sm">
                      {option.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
            <Button variant="outline" onClick={handleBack} className="w-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </div>
        )}

        {/* Step 3: Select Implementation */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Escolha a implementação</h3>
              <p className="text-sm text-muted-foreground">
                Selecione qual tecnologia/API será utilizada
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {getImplementationOptions().map((option) => (
                <Card
                  key={option.value}
                  className="cursor-pointer hover:border-primary transition-colors"
                  onClick={() => handleImplementationSelect(option.value)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          {option.label}
                          {option.recommended && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                              Recomendado
                            </span>
                          )}
                        </CardTitle>
                        <CardDescription className="text-sm mt-1">
                          {option.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
            <Button variant="outline" onClick={handleBack} className="w-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </div>
        )}

        {/* Step 4: Configure Credentials */}
        {step === 4 && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Configure as credenciais</h3>
              <p className="text-sm text-muted-foreground">
                Preencha os dados de acesso do seu provedor
              </p>
            </div>

            {/* Render specific form based on selection */}
            {selectedChannel === 'WHATSAPP' &&
              selectedProvider === 'WHATICKET' &&
              selectedImplementation === 'WHATSAPP_CLOUD' && (
                <WhaticketOfficialForm onSuccess={handleFormSuccess} onCancel={handleBack} />
              )}

            {selectedChannel === 'WHATSAPP' &&
              selectedProvider === 'NOTIFICAMEHUB' &&
              selectedImplementation === 'WHATSAPP_CLOUD' && (
                <NotificamehubOfficialForm onSuccess={handleFormSuccess} onCancel={handleBack} />
              )}

            {/* Placeholder for other combinations */}
            {!(
              (selectedChannel === 'WHATSAPP' &&
                selectedProvider === 'WHATICKET' &&
                selectedImplementation === 'WHATSAPP_CLOUD') ||
              (selectedChannel === 'WHATSAPP' &&
                selectedProvider === 'NOTIFICAMEHUB' &&
                selectedImplementation === 'WHATSAPP_CLOUD')
            ) && (
              <Card className="border-dashed">
                <CardContent className="pt-6 text-center">
                  <p className="text-muted-foreground mb-4">
                    Formulário para esta combinação ainda não implementado
                  </p>
                  <Button variant="outline" onClick={handleBack}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Voltar
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
